import React, { useEffect, useState } from "react";
import { IPaymentMethod } from "../models/IPaymentMethod";
import {
  getPaymentMethods,
  getRestaurantPaymentMethods,
  assignPaymentMethod,
  removePaymentMethod,
} from "../services/paymentMethod";
import { getRestaurantList } from "../services/restaurant";
import { RestaurantList } from "../models/IRestaurantDetail";

const PAYMENT_ICONS: Record<string, string> = {
  nakit: "bi-cash-stack",
  kredi: "bi-credit-card-2-front-fill",
  kart: "bi-credit-card-2-front-fill",
  online: "bi-globe2",
  kapı: "bi-door-open-fill",
  havale: "bi-bank2",
  eft: "bi-bank2",
  yemek: "bi-gift-fill",
  çek: "bi-file-earmark-text-fill",
};

const getIcon = (name: string) => {
  const lower = name.toLowerCase();
  for (const [key, icon] of Object.entries(PAYMENT_ICONS)) {
    if (lower.includes(key)) return icon;
  }
  return "bi-wallet2";
};

const PALETTE = [
  "linear-gradient(135deg,#6366f1,#4f46e5)",
  "linear-gradient(135deg,#10b981,#059669)",
  "linear-gradient(135deg,#f59e0b,#d97706)",
  "linear-gradient(135deg,#ef4444,#dc2626)",
  "linear-gradient(135deg,#3b82f6,#2563eb)",
  "linear-gradient(135deg,#8b5cf6,#7c3aed)",
  "linear-gradient(135deg,#ec4899,#db2777)",
  "linear-gradient(135deg,#14b8a6,#0d9488)",
];

export default function AdminPaymentMethodsPage() {
  const [restaurantList, setRestaurantList] = useState<RestaurantList>();
  const [selectedId, setSelectedId]         = useState("");
  const [allMethods, setAllMethods]         = useState<IPaymentMethod[]>([]);
  const [activeMethods, setActiveMethods]   = useState<Set<string>>(new Set());
  const [toggling, setToggling]             = useState<Set<string>>(new Set());
  const [loading, setLoading]               = useState(false);
  const [error, setError]                   = useState("");

  useEffect(() => {
    getRestaurantList().then(res => {
      if (res.data) setRestaurantList(res.data);
    });
    getPaymentMethods().then(res => setAllMethods(res.data));
  }, []);

  useEffect(() => {
    if (!selectedId) { setActiveMethods(new Set()); return; }
    setLoading(true);
    setError("");
    getRestaurantPaymentMethods(selectedId)
      .then(res => setActiveMethods(new Set(res.data.map(m => m.id))))
      .catch(() => setError("Ödeme yöntemleri yüklenemedi."))
      .finally(() => setLoading(false));
  }, [selectedId]);

  const handleToggle = (method: IPaymentMethod) => {
    if (!selectedId || toggling.has(method.id)) return;
    const isActive = activeMethods.has(method.id);
    setToggling(prev => new Set(prev).add(method.id));

    const req = isActive
      ? removePaymentMethod(selectedId, method.id)
      : assignPaymentMethod(selectedId, method.id);

    req
      .then(() => {
        setActiveMethods(prev => {
          const next = new Set(prev);
          if (isActive) next.delete(method.id);
          else next.add(method.id);
          return next;
        });
      })
      .catch(() => setError(`"${method.paymentMethodNameTr}" güncellenemedi.`))
      .finally(() => {
        setToggling(prev => {
          const next = new Set(prev);
          next.delete(method.id);
          return next;
        });
      });
  };

  const selectedRestaurantName = restaurantList?.items.find(r => r.id === selectedId)?.restourantName ?? "";
  const activeCount = activeMethods.size;
  const totalCount  = allMethods.length;

  return (
    <div style={{ minHeight: "100vh", background: "#f4f6fb" }}>
      {/* Header */}
      <div
        style={{
          background: "linear-gradient(135deg,#6366f1 0%,#4f46e5 60%,#3730a3 100%)",
          padding: "28px 24px 32px",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div style={{ position: "absolute", top: "-40px", right: "-40px", width: "180px", height: "180px", borderRadius: "50%", background: "rgba(255,255,255,0.08)" }} />
        <div style={{ position: "absolute", bottom: "-60px", left: "30%", width: "250px", height: "250px", borderRadius: "50%", background: "rgba(255,255,255,0.05)" }} />
        <div style={{ position: "relative", zIndex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "8px" }}>
            <div style={{ width: "42px", height: "42px", borderRadius: "12px", background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <i className="bi bi-wallet-fill" style={{ fontSize: "1.3rem", color: "#fff" }}></i>
            </div>
            <h1 style={{ margin: 0, color: "#fff", fontSize: "1.5rem", fontWeight: 800 }}>
              Ödeme Yöntemleri — Admin
            </h1>
          </div>
          <p style={{ margin: 0, color: "rgba(255,255,255,0.75)", fontSize: "0.9rem" }}>
            Restoran seçin ve ödeme yöntemlerini yönetin
          </p>
          {selectedId && !loading && (
            <div style={{ marginTop: "16px", display: "inline-flex", alignItems: "center", gap: "8px", background: "rgba(255,255,255,0.15)", borderRadius: "20px", padding: "6px 14px" }}>
              <span style={{ color: "#fff", fontSize: "0.85rem", fontWeight: 600 }}>
                {activeCount} / {totalCount} aktif
              </span>
              <div style={{ height: "6px", width: "80px", borderRadius: "3px", background: "rgba(255,255,255,0.3)", overflow: "hidden" }}>
                <div style={{ height: "100%", borderRadius: "3px", background: "#fff", width: totalCount > 0 ? `${(activeCount / totalCount) * 100}%` : "0%", transition: "width 0.4s ease" }} />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div style={{ padding: "16px 16px 0", maxWidth: "700px", margin: "0 auto" }}>

        {/* Restaurant selector */}
        <div
          style={{
            background: "#fff",
            borderRadius: "18px",
            boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
            padding: "20px",
            marginBottom: "16px",
          }}
        >
          <label style={{ fontSize: "0.78rem", fontWeight: 700, color: "#374151", display: "block", marginBottom: "8px", textTransform: "uppercase", letterSpacing: "0.05em" }}>
            <i className="bi bi-building me-1"></i>Restoran
          </label>
          <select
            className="form-select form-select-lg"
            style={{ borderRadius: "12px", border: "1.5px solid #dee2e6", boxShadow: "0 2px 8px rgba(0,0,0,0.06)", fontWeight: 500 }}
            value={selectedId}
            onChange={e => setSelectedId(e.target.value)}
          >
            <option value="" disabled>Restoran seçiniz...</option>
            {restaurantList?.items.map(r => (
              <option key={r.id} value={r.id}>{r.restourantName}</option>
            ))}
          </select>
        </div>

        {/* Error */}
        {error && (
          <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: "12px", padding: "12px 16px", marginBottom: "16px", display: "flex", alignItems: "center", gap: "8px" }}>
            <i className="bi bi-exclamation-circle-fill" style={{ color: "#ef4444" }}></i>
            <span style={{ color: "#dc2626", fontSize: "0.9rem" }}>{error}</span>
            <button onClick={() => setError("")} style={{ marginLeft: "auto", background: "none", border: "none", color: "#9ca3af", cursor: "pointer" }}>
              <i className="bi bi-x-lg"></i>
            </button>
          </div>
        )}

        {/* Body */}
        {!selectedId ? (
          <div style={{ textAlign: "center", padding: "60px 0", color: "#9ca3af" }}>
            <i className="bi bi-shop" style={{ fontSize: "2.5rem", display: "block", marginBottom: "10px" }}></i>
            <span style={{ fontSize: "0.9rem" }}>Ödeme yöntemlerini görmek için restoran seçin.</span>
          </div>
        ) : loading ? (
          <div style={{ textAlign: "center", padding: "60px 0" }}>
            <div style={{ width: "48px", height: "48px", borderRadius: "50%", border: "4px solid #e0e7ff", borderTopColor: "#6366f1", animation: "spin 0.8s linear infinite", margin: "0 auto 16px" }} />
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            <p style={{ color: "#6b7280", margin: 0 }}>Yükleniyor...</p>
          </div>
        ) : allMethods.length === 0 ? (
          <div style={{ textAlign: "center", padding: "60px 20px", background: "#fff", borderRadius: "20px", boxShadow: "0 2px 20px rgba(0,0,0,0.06)" }}>
            <i className="bi bi-wallet2" style={{ fontSize: "3rem", color: "#d1d5db" }}></i>
            <p style={{ color: "#9ca3af", marginTop: "12px", fontWeight: 500 }}>Henüz ödeme yöntemi tanımlanmamış.</p>
          </div>
        ) : (
          <div style={{ paddingTop: "8px", paddingBottom: "32px" }}>
            {selectedRestaurantName && (
              <p style={{ color: "#6b7280", fontSize: "0.82rem", marginBottom: "12px", paddingLeft: "4px" }}>
                <i className="bi bi-shop me-1"></i>
                <strong style={{ color: "#374151" }}>{selectedRestaurantName}</strong> için aktif ödeme yöntemleri
              </p>
            )}
            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              {allMethods.map((method, idx) => {
                const isActive = activeMethods.has(method.id);
                const isBusy   = toggling.has(method.id);
                const gradient = PALETTE[idx % PALETTE.length];

                return (
                  <div
                    key={method.id}
                    style={{
                      background: "#fff",
                      borderRadius: "18px",
                      boxShadow: isActive ? "0 4px 20px rgba(99,102,241,0.15)" : "0 2px 12px rgba(0,0,0,0.06)",
                      border: isActive ? "1.5px solid #c7d2fe" : "1.5px solid #f1f5f9",
                      transition: "all 0.25s",
                      padding: "16px 18px",
                      display: "flex",
                      alignItems: "center",
                      gap: "14px",
                    }}
                  >
                    {/* Icon */}
                    <div
                      style={{
                        width: "48px", height: "48px", borderRadius: "14px",
                        background: isActive ? gradient : "#f3f4f6",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        flexShrink: 0, transition: "all 0.25s",
                      }}
                    >
                      <i
                        className={`bi ${getIcon(method.paymentMethodNameTr)}`}
                        style={{ fontSize: "1.35rem", color: isActive ? "#fff" : "#9ca3af", transition: "color 0.25s" }}
                      ></i>
                    </div>

                    {/* Text */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: "0.95rem", color: isActive ? "#111827" : "#6b7280", transition: "color 0.25s", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {method.paymentMethodNameTr}
                      </div>
                      <div style={{ fontSize: "0.78rem", color: "#9ca3af", marginTop: "2px" }}>
                        {method.paymentMethodNameEn}
                      </div>
                      {isActive && (
                        <span style={{ display: "inline-block", marginTop: "5px", fontSize: "0.7rem", fontWeight: 700, color: "#4f46e5", background: "#eef2ff", borderRadius: "20px", padding: "2px 8px" }}>
                          Aktif
                        </span>
                      )}
                    </div>

                    {/* Toggle */}
                    <button
                      onClick={() => handleToggle(method)}
                      disabled={isBusy}
                      title={isActive ? "Devre dışı bırak" : "Etkinleştir"}
                      style={{
                        position: "relative",
                        width: "52px", height: "28px",
                        borderRadius: "14px", border: "none",
                        background: isBusy ? "#e5e7eb" : isActive ? "#6366f1" : "#e5e7eb",
                        cursor: isBusy ? "not-allowed" : "pointer",
                        transition: "background 0.25s",
                        flexShrink: 0,
                        padding: 0,
                        boxShadow: isActive ? "0 2px 8px rgba(99,102,241,0.4)" : "none",
                      }}
                    >
                      <span
                        style={{
                          position: "absolute",
                          top: "3px",
                          left: isBusy ? "13px" : isActive ? "26px" : "3px",
                          width: "22px", height: "22px",
                          borderRadius: "50%",
                          background: "#fff",
                          boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
                          transition: "left 0.25s",
                          display: "flex", alignItems: "center", justifyContent: "center",
                        }}
                      >
                        {isBusy && (
                          <span style={{ width: "10px", height: "10px", borderRadius: "50%", border: "2px solid #9ca3af", borderTopColor: "#6366f1", display: "inline-block", animation: "spin 0.7s linear infinite" }} />
                        )}
                      </span>
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
